import React, { useEffect } from 'react';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { Helmet } from 'react-helmet';
import { replace } from 'connected-react-router';
import { useParams } from 'react-router-dom';
import { SentryBoundary } from '@saucelabs/sl-component-sentry';
import { Breadcrumbs } from '@saucelabs/ds-breadcrumbs';
import { constants as sentryConstants } from '@saucelabs/sl-util-sentry';
import { ContentLoader } from '@saucelabs/sl-component-loader';
import { Tabs, Tab } from '@saucelabs/sl-component-tabs';
import {
  IconDevicePhoneFill,
  IconDeviceVmFill,
} from '@saucelabs/ds-icons';
import {
  constants as appStorageConstants,
  records as appStorageRecords,
} from '@saucelabs/sl-module-app-storage';
import { isSupportedFeature, constants as features } from '@saucelabs/sl-component-supported-feature';

import * as globalActions from '../../../actions';
import { RecentsModal, ShowRecentsButton } from '../../presets';
import {
  CONFIGURATION_MOBILE_APP_TAB_URL_PATHS,
  CONFIGURATION_TEST_TYPES,
  TEST_CONFIGURATION_TABS,
} from '../../../constants';
import { ValueOf } from '../../../globalTypes';
import { NAVIGATION_EVENTS, trackEvent } from '../../../tracking';
import VirtualDeviceConfigurator from '../../../components/VirtualDeviceConfigurator';
import {
  actions as testConfigurationActions,
  hooks as testConfigurationHooks,
  selectors as testConfigurationSelectors,
} from '../../testConfiguration';
import { utils as mobileAppSelectionUtils } from '../../mobileAppSelection';
import { GlobalState } from '../../../reducers';
import AppSearchAndSelect from './AppSearchAndSelect';
import TunnelSelector from '../../../components/TunnelSelector';
import ContainerStyles from '../../../containers/configurator/TestingContainer.styles';
import Styles from './MobileAppSelection.styles';
import {
  selectors as realDeviceSelectionSelectors,
  MobileSelectionContainer,
} from '../../realDeviceSelection';

const crumbsList = [{ name: 'Live' }, { name: 'Mobile App' }];

export type TabKind = keyof typeof CONFIGURATION_MOBILE_APP_TAB_URL_PATHS;
export type TabPath = ValueOf<typeof CONFIGURATION_MOBILE_APP_TAB_URL_PATHS> | undefined;
export type PathParamsType = {
  fileId?: string;
  tab?: TabPath;
}

const tabList = [
  ...(isSupportedFeature(features.RDC) ?
    [TEST_CONFIGURATION_TABS.MOBILE_REAL] : []),
  ...(isSupportedFeature(features.LIVE_TESTING_EMUSIM) ?
    [TEST_CONFIGURATION_TABS.MOBILE_VIRTUAL] : []),
] as const;

const getTabForPath = (path: TabPath) => (
  Object.keys(CONFIGURATION_MOBILE_APP_TAB_URL_PATHS)
    .find((key) => CONFIGURATION_MOBILE_APP_TAB_URL_PATHS[key] === path)
);

const getTabHeaderContent = (tabKey: TabKind) => ({
  [TEST_CONFIGURATION_TABS.MOBILE_REAL]: {
    Icon: IconDevicePhoneFill,
    content: <MobileSelectionContainer />,
    label: 'Mobile Real',
  },
  [TEST_CONFIGURATION_TABS.MOBILE_VIRTUAL]: {
    Icon: IconDeviceVmFill,
    content: <VirtualDeviceConfigurator />,
    label: 'Mobile Virtual',
  },
})[tabKey];

const context = classNames.bind(ContainerStyles);

const MobileAppSelection: React.FC = () => {
  const dispatch = useDispatch<ReduxDispatcher>();
  const virtualDeviceOptionsLoaded = useSelector<GlobalState, boolean>(
    testConfigurationSelectors.virtualDeviceOptionsLoadedSelector,
  );
  const pinnedDevicesLoaded = useSelector<GlobalState, boolean>(
    realDeviceSelectionSelectors.pinnedDeviceCompositeIdsLoadedSelector,
  );
  const mobileAppConfiguration = useSelector<GlobalState, appStorageRecords.AppStorageFile | null>(
    testConfigurationSelectors.mobileAppConfigurationSelector,
  );
  const { fileId, tab: tabPath } = useParams<PathParamsType>();

  const onTabChange = (tab: TabKind) => {
    trackEvent(NAVIGATION_EVENTS.CONFIGURATION_TABS[tab]);

    dispatch(replace(
      mobileAppSelectionUtils.getUrlForMobileSelection(mobileAppConfiguration, undefined, tab),
    ));
  };

  testConfigurationHooks.useCheckIfCurrentTunnelAvailable();

  useEffect(() => {
    // Reset the mobile app on mount
    // only in case there is a "selected" in state and another is requested
    // to avoid displaying an "old" selection
    if (mobileAppConfiguration && (mobileAppConfiguration.id !== fileId)) {
      void dispatch(testConfigurationActions.updateTestConfigValue({
        mobileAppConfiguration: null,
      }));
    }
  // we ignoring deps by pupose because it should really only happen on mount
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    dispatch(testConfigurationActions.updateTestConfigValue({
      testType: CONFIGURATION_TEST_TYPES.APP,
    }));
  }, [dispatch]);

  useEffect(() => {
    if (!virtualDeviceOptionsLoaded && isSupportedFeature(features.LIVE_TESTING_EMUSIM)) {
      void dispatch(testConfigurationActions.fetchVirtualDeviceOptions());
    }
  }, [dispatch, virtualDeviceOptionsLoaded]);

  useEffect(() => {
    if (!pinnedDevicesLoaded) {
      void dispatch(globalActions.getMetadata());
    }
  }, [pinnedDevicesLoaded, dispatch]);

  const showMultipleTabs = (
    tabList.length > 1 &&
    mobileAppConfiguration?.kind !== appStorageConstants.APP_STORAGE_KIND.IOS
  );

  const showLoader = !(
    virtualDeviceOptionsLoaded || !isSupportedFeature(features.LIVE_TESTING_EMUSIM)
  );

  return (
    <SentryBoundary owner={sentryConstants.TEAMS.LIVE_TESTING}>
      <Helmet>
        <title>Live | Mobile App</title>
      </Helmet>

      <Breadcrumbs list={crumbsList}>
        <ShowRecentsButton />
      </Breadcrumbs>

      <RecentsModal />

      <div data-qa="app-testing-container">
        <div className="flex px-2 py-2.5 gap-2">
          <AppSearchAndSelect />

          {isSupportedFeature(features.LIVE_TESTING_TUNNELS) && (
            <TunnelSelector />
          )}
        </div>

        <ContentLoader show={showLoader}>
          <Tabs
            activeKey={getTabForPath(tabPath)}
            animation={false}
            defaultActiveKey={TEST_CONFIGURATION_TABS.MOBILE_REAL}
            id="mobile-app-selection-tabs"
            mountOnEnter
            onSelect={onTabChange}
            unmountOnExit
          >
            {tabList.map((tabKey: TabKind) => {
              const { content, Icon, label } = getTabHeaderContent(tabKey);

              return (
                <Tab
                  key={tabKey}
                  eventKey={tabKey}
                  mountOnEnter
                  tabClassName={classNames('tall origin-top overflow-hidden transition-all duration-300 ease-linear',
                    Styles.tab,
                    { [Styles.hidden]: !showMultipleTabs },
                  )}
                  title={(
                    <div
                      className={ContainerStyles['tab-title']}
                      role="presentation"
                    >
                      <Icon
                        aria-hidden
                        className={context(ContainerStyles['tab-title'], 'align-middle fill-current h-2 mr-1.5 text-gray-500 w-2')}
                      />
                      <span className={context(ContainerStyles['tab-title'], 'align-middle normal-case')}>
                        {label}
                      </span>
                    </div>
                  )}
                >
                  <div className={ContainerStyles['margin-top-fix']}>
                    {content}
                  </div>
                </Tab>
              );
            })}
          </Tabs>
        </ContentLoader>
      </div>
    </SentryBoundary>
  );
};

export default MobileAppSelection;
